class LuxThemeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button) return;
    this.sync();
    this.button.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const current = document.documentElement.dataset.theme;
    const next = current === 'dark' ? 'light' : 'dark';
    document.documentElement.dataset.theme = next;
    localStorage.setItem('lux-theme', next);
    document.querySelectorAll('lux-theme-toggle').forEach((toggle) => toggle.sync());
  }

  sync() {
    if (!this.button) this.button = this.querySelector('button');
    if (!this.button) return;
    const current = document.documentElement.dataset.theme;
    const nextLabel = current === 'dark' ? this.button.dataset.lightLabel : this.button.dataset.darkLabel;
    this.button.setAttribute('aria-label', nextLabel);
    this.button.setAttribute('title', nextLabel);
  }
}

customElements.define('lux-theme-toggle', LuxThemeToggle);

class LuxCollectionSort extends HTMLElement {
  connectedCallback() {
    this.select = this.querySelector('select');
    this.form = this.querySelector('form');
    if (!this.select || !this.form) return;
    this.select.addEventListener('change', () => this.form.submit());
  }
}

customElements.define('lux-collection-sort', LuxCollectionSort);

function initLuxMobileMenu(menu) {
  if (menu.dataset.accessibilityReady === 'true') return;
  menu.dataset.accessibilityReady = 'true';

  const summary = menu.querySelector(':scope > summary');
  const panel = menu.querySelector(':scope > .lux-header__mobile-panel');
  if (!summary || !panel) return;

  const openLabel = summary.dataset.openLabel;
  const closeLabel = summary.dataset.closeLabel;
  const focusableSelector =
    'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const sync = () => {
    const isOpen = menu.open;
    summary.setAttribute('aria-label', isOpen ? closeLabel : openLabel);
    summary.setAttribute('aria-expanded', String(isOpen));
    panel.setAttribute('aria-hidden', String(!isOpen));
    if (isOpen) {
      requestAnimationFrame(() => {
        panel.querySelector(focusableSelector)?.focus();
      });
    }
  };

  menu.addEventListener('toggle', sync);
  menu.addEventListener('keydown', (event) => {
    if (!menu.open) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      menu.open = false;
      summary.focus();
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = [summary, ...panel.querySelectorAll(focusableSelector)].filter(
      (element) => !element.hidden && element.getClientRects().length > 0
    );
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  window.addEventListener('resize', () => {
    if (window.innerWidth > 1120 && menu.open) menu.open = false;
  });

  sync();
}

document.querySelectorAll('details.lux-header__mobile-menu').forEach(initLuxMobileMenu);

class LuxStudioCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-carousel-slide]')];
    this.dots = [...this.querySelectorAll('[data-carousel-dot]')];
    this.currentLabel = this.querySelector('[data-carousel-current]');
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.interval = Math.max(5000, Number(this.dataset.interval) || 7000);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (this.slides.length < 2) return;

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.show(this.index - 1, true));
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.show(this.index + 1, true));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => this.show(Number(dot.dataset.carouselDot), true));
    });

    this.addEventListener('mouseenter', () => this.pause());
    this.addEventListener('mouseleave', () => this.start());
    this.addEventListener('focusin', () => this.pause());
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.start();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pause();
      else this.start();
    });

    this.show(this.index, false);
    this.start();
  }

  disconnectedCallback() {
    this.pause();
  }

  show(nextIndex, userInitiated = false) {
    const length = this.slides.length;
    this.index = (nextIndex + length) % length;

    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      slide.toggleAttribute('inert', !active);
    });
    this.dots.forEach((dot, dotIndex) => {
      const active = dotIndex === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', String(active));
    });
    if (this.currentLabel) this.currentLabel.textContent = String(this.index + 1).padStart(2, '0');

    if (userInitiated) {
      this.pause();
      this.start();
    }
  }

  start() {
    this.pause();
    if (this.dataset.autoplay !== 'true' || this.reduceMotion.matches || document.hidden) return;
    this.timer = window.setInterval(() => this.show(this.index + 1), this.interval);
  }

  pause() {
    if (this.timer) window.clearInterval(this.timer);
    this.timer = null;
  }
}

if (!customElements.get('lux-studio-carousel')) {
  customElements.define('lux-studio-carousel', LuxStudioCarousel);
}

class LuxCartDrawer extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;

    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-cart-close]')) {
        this.close();
        return;
      }

      const quantityButton = event.target.closest('[data-cart-line][data-cart-quantity]');
      if (quantityButton) this.changeQuantity(quantityButton);
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.close();
    });

    this.dialog.addEventListener('close', () => {
      this.openTrigger?.focus();
    });
  }

  open(trigger) {
    if (!this.dialog) return;
    this.openTrigger = trigger || document.activeElement;
    if (!this.dialog.open) this.dialog.showModal();
  }

  close() {
    if (this.dialog?.open) this.dialog.close();
  }

  async changeQuantity(button) {
    if (this.loading) return;
    this.loading = true;
    button.disabled = true;

    try {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: Number(button.dataset.cartLine),
          quantity: Number(button.dataset.cartQuantity),
        }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      await this.refresh(true);
    } catch (error) {
      button.disabled = false;
      this.loading = false;
      const status = this.querySelector('[data-cart-status]');
      if (status) status.textContent = this.dataset.updateError || 'That quantity is not available.';
    }
  }

  async refresh(openAfter = false, trigger) {
    const [sectionResponse, cartResponse] = await Promise.all([
      fetch(`${this.root}?section_id=cart-drawer`),
      fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' } }),
    ]);

    if (!sectionResponse.ok || !cartResponse.ok) throw new Error('Cart refresh failed');

    const sectionMarkup = await sectionResponse.text();
    const cart = await cartResponse.json();
    const documentFragment = new DOMParser().parseFromString(sectionMarkup, 'text/html');
    const nextDrawer = documentFragment.querySelector('lux-cart-drawer');
    if (!nextDrawer) throw new Error('Cart drawer section missing');

    document.querySelectorAll('.lux-header__cart-count').forEach((count) => {
      count.textContent = cart.item_count;
    });

    this.replaceWith(nextDrawer);
    if (openAfter) nextDrawer.open(trigger || this.openTrigger);
    return nextDrawer;
  }

  get root() {
    return window.Shopify?.routes?.root || '/';
  }
}

customElements.define('lux-cart-drawer', LuxCartDrawer);

class LuxProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.lux-product-form');
    this.quantity = this.querySelector('input[name="quantity"]');
    this.addButton = this.querySelector('.lux-product__add');
    this.price = this.querySelector('[data-product-price]');
    this.status = this.querySelector('[data-product-status]');
    this.authDialog = this.querySelector('[data-auth-dialog]');

    this.querySelector('[data-auth-open]')?.addEventListener('click', () => {
      this.authDialog?.showModal();
    });

    this.authDialog?.addEventListener('click', (event) => {
      if (event.target === this.authDialog) this.authDialog.close();
    });

    if (!this.form || !this.addButton) return;

    this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.adjustQuantity(-1));
    this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.adjustQuantity(1));

    this.querySelectorAll('input[name="id"][type="radio"]').forEach((radio) => {
      radio.addEventListener('change', () => this.selectVariant(radio));
    });

    this.form.addEventListener('submit', (event) => {
      if (!event.submitter?.matches('.lux-product__add')) return;
      event.preventDefault();
      this.addToCart();
    });
  }

  adjustQuantity(change) {
    if (!this.quantity) return;
    const current = Number(this.quantity.value) || 1;
    this.quantity.value = Math.max(1, current + change);
  }

  selectVariant(radio) {
    if (this.price) this.price.textContent = radio.dataset.price;
    const available = radio.dataset.available === 'true';
    this.addButton.disabled = !available;
    const label = this.addButton.querySelector('[data-add-label]');
    if (label) label.textContent = available ? this.dataset.addLabel : this.dataset.soldOutLabel;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', radio.value);
    window.history.replaceState({}, '', url);
  }

  async addToCart() {
    if (this.loading) return;
    this.loading = true;
    this.addButton.disabled = true;
    if (this.status) this.status.textContent = '';

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: new FormData(this.form),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add item');

      const drawer = document.querySelector('lux-cart-drawer');
      if (!drawer) throw new Error('Cart drawer unavailable');
      await drawer.refresh(true, this.addButton);
    } catch (error) {
      if (this.status) this.status.textContent = error.message;
    } finally {
      this.loading = false;
      const selectedVariant =
        this.form.querySelector('input[name="id"]:checked') ||
        this.form.querySelector('input[name="id"]');
      this.addButton.disabled = selectedVariant?.dataset.available === 'false';
    }
  }
}

customElements.define('lux-product', LuxProduct);

document.addEventListener('click', (event) => {
  const cartTrigger = event.target.closest('[data-cart-open]');
  if (cartTrigger) {
    event.preventDefault();
    document.querySelector('lux-cart-drawer')?.open(cartTrigger);
    return;
  }

  const closeButton = event.target.closest('[data-filter-close]');
  if (!closeButton) return;
  closeButton.closest('details.lux-collection-filters')?.removeAttribute('open');
});

document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('lux-theme-toggle').forEach((toggle) => toggle.sync());
});
